/**
 * AttentionStrip — the cross-plane "needs attention" row at the top of the
 * mission-control dashboard.
 *
 * It is PURELY presentational: the page derives the items from whatever plane
 * summaries it already fetched (so the strip self-respects per-plane
 * permissions — a card the operator can't see contributes nothing), and the
 * strip renders nothing at all when every count is zero (all green).
 */
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

export interface AttentionItem {
  key: string;
  label: string;
  count: number;
  tone: "danger" | "warning";
  href: string;
}

const TONE: Record<AttentionItem["tone"], string> = {
  danger: "border-danger/40 bg-danger/10 text-danger hover:bg-danger/20",
  warning: "border-warning/40 bg-warning/10 text-warning hover:bg-warning/20",
};

export function AttentionStrip({ items }: { items: AttentionItem[] }) {
  const live = items.filter((i) => i.count > 0);
  if (live.length === 0) return null;

  // A slim OPEN band — no outer box (the minimal redesign: the chips carry
  // their own tint, a border around them was just one more rectangle).
  return (
    <div role="status" className="mb-6 flex flex-wrap items-center gap-2">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-danger" strokeWidth={1.5} />
      {live.map((i) => (
        <Link
          key={i.key}
          to={i.href}
          className={
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition " +
            TONE[i.tone]
          }
        >
          <span className="tabular-nums font-semibold">{i.count}</span>
          <span>{i.label}</span>
        </Link>
      ))}
    </div>
  );
}
